import { put, take, fork, call, select } from "redux-saga/effects";
import {
  enableScopeLoading,
  disableScopeLoading,
} from "../scope-loading/actions";
import { ScopeErrorAction } from "../scope-error/types";
import { ContractAction, ContractScope } from "./types";
import { cleanUpContract, setContractRecord } from "./actions";
import * as API from "../../service/api";
import { mapApiContract } from "./helper";
import { RootState } from "../root-reducer";
import { setContractRecords } from "../user/actions";
import { ContractStatus } from "../user/types";
import toast from "react-hot-toast";

function* fetchContractRecords(): Generator<any, void, any> {
  while (true) {
    const { payload = "" } = yield take(ContractAction.GET_CONTRACT_RECORD);
    yield put(enableScopeLoading(ContractScope));
    try {
      yield put(cleanUpContract());
      if (!payload) throw new Error("Id is required");
      const data = yield call(API.contract.getPlanById, payload);
      yield put(setContractRecord(mapApiContract(data)));
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ContractScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ContractScope));
    }
  }
}

function* cancelContractFlow(): Generator<any, void, any> {
  while (true) {
    const {
      payload = {},
      planId = "",
      callback,
    } = yield take(ContractAction.CANCEL_CONTRACT);
    yield put(enableScopeLoading(`cancel-${ContractScope}`));
    try {
      const dealerName = yield select(
        (state: RootState) =>
          state?.user?.selectedOrg?.["name"] ||
          state?.user?.selectedOrg?.associated_dealers?.[0]?.id ||
          "N/A"
      );
      payload.org_name = dealerName;
      yield call(API.contract.cancelPlan, planId, payload);
      const contract = yield select((state: RootState) => state.user.contracts);
      const updatedContracts = yield new Promise((resolve, reject) => {
        try {
          const data = contract.data.map((item) =>
            item.id === planId
              ? { ...item, status: ContractStatus.CancelRequestDealerReview }
              : item
          );
          resolve(data);
        } catch (error) {
          reject(error);
        }
      });
      yield put(
        setContractRecords({
          data: updatedContracts,
          info: contract.info,
        })
      );
      callback();
    } catch (error) {
      console.log("error:", error);
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: `cancel-${ContractScope}`,
        error,
      });
    } finally {
      yield put(disableScopeLoading(`cancel-${ContractScope}`));
    }
  }
}

function* calculateCancelAmountFlow(): Generator<any, void, any> {
  while (true) {
    yield take(ContractAction.CANCEL_CONTRACT_AMOUNT_CLACULATE);
    yield put(enableScopeLoading(`${ContractScope}-calculate-amount`));
    try {
      const planId = yield select((state: RootState) => state.contract.id);
      const { data = {}, claims = [] } = yield call(
        API.contract.cancelPlanCalculateAmount,
        planId
      );
      const amount = data?.final_refund;
      if (claims.length > 0) {
        yield put(
          setContractRecord({
            isActiveClaim: true,
          })
        );
      } else if (amount) {
        yield put(
          setContractRecord({
            calculatedRefundAmount: amount,
          })
        );
      }
    } catch (error) {
      console.log("error:", error);
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: `${ContractScope}-calculate-amount`,
        error,
      });
    } finally {
      yield put(disableScopeLoading(`${ContractScope}-calculate-amount`));
    }
  }
}

function* sendEmailFlow() {
  while (true) {
    const { payload, callBack } = yield take(
      ContractAction.CLAIM_FILE_SEND_EMAIL
    );
    yield put(enableScopeLoading(`contact-${ContractScope}`));
    try {
      const planName = yield select(
        (state: RootState) => state?.contract?.name
      );
      const userName = yield select((state: RootState) => {
        const user = state?.user;
        return user?.name || user?.userName || "N/A";
      });

      if (!planName) throw new Error("Plan Number not found.");

      let { data = {} } = payload;

      const response = yield call(API.contract.sendEmail, {
        ...data,
        csr: userName,
      });
      console.log("response: ", response);
      if (response) {
        callBack();
      }
    } catch (error: any) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: `contact-${ContractScope}`,
        error: error.response?.data?.message || error.message,
      });
      toast.error(error.response?.data?.message || error.message);
    } finally {
      yield put(disableScopeLoading(`contact-${ContractScope}`));
    }
  }
}

function* submitContractTicketFlow() {
  while (true) {
    const { payload, callBack } = yield take(ContractAction.SUBMIT_TICKET);
    yield put(enableScopeLoading(`contact-${ContractScope}`));
    try {
      const {
        name = "N/A", // contract number
        customerFirstName = "",
        customerLastName = "",
      } = yield select((state: RootState) => state?.contract);

      const dealerName = yield select(
        (state: RootState) =>
          state?.user?.selectedOrg?.name ||
          state?.user?.selectedOrg?.associated_dealers?.[0]?.alias ||
          "N/A"
      );

      const userEmail = yield select((state: RootState) => state?.user?.email);

      let { data = {} } = payload;
      let { reason = "", comment = "" } = data;

      const res = yield call(API.deskTicket.contactTicket, {
        subject: `Dealer: ${dealerName} Plan: ${name}`,
        description: `
        Customer Name: ${customerFirstName} ${customerLastName}
        <br>
        ${reason ? `Reason: ${reason}<br>` : ""}
        Message: 
        <br>
        ${comment.replace(/\n/g, "<br>")}
        `,
        contact_email: userEmail || "no@email.com",
      });
      if (res.id) {
        callBack();
      }
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: `contact-${ContractScope}`,
        error,
      });
    } finally {
      yield put(disableScopeLoading(`contact-${ContractScope}`));
    }
  }
}

export default function* contractSaga(): Generator<any, void, any> {
  yield fork(fetchContractRecords);
  yield fork(cancelContractFlow);
  yield fork(calculateCancelAmountFlow);
  yield fork(sendEmailFlow);
  yield fork(submitContractTicketFlow);
}
